import type { NavListProps, NavSubListProps } from '../types';

import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useState, useEffect, useCallback } from 'react';
import { isEqualPath, isActiveLink, mergeClasses, isExternalLink } from 'minimal-shared/utils';

import { usePathname } from '@/routes/hooks';

import { NavItem } from './nav-desktop-item';
import { Nav, NavLi, NavUl, NavDropdown } from '../components';

export function NavList({ data, className, ...other }: NavListProps) {
  const pathname = usePathname();
  const navItemRef = useRef<HTMLDivElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const [indicatorLeft, setIndicatorLeft] = useState<number | null>(null);

  const isActive = isActiveLink(
    pathname,
    data.category.path,
    data.category.deepMatch ?? !!data.children
  );

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.children && navItemRef.current && navContainerRef.current) {
      const itemRect = navItemRef.current.getBoundingClientRect();
      const containerRect = navContainerRef.current.getBoundingClientRect();

      const center = itemRect.left + itemRect.width / 2;
      const offsetLeft = center - containerRect.left;
      const containerWidth = containerRect.width;

      const percentage = (offsetLeft / containerWidth) * 100;

      setIndicatorLeft(percentage);
      onOpen();
    }
  }, [data.children, onOpen, navContainerRef]);

  const renderNavItem = () => (
    <div ref={navItemRef} onMouseEnter={handleOpenMenu} onMouseLeave={onClose}>
      <NavItem
        path={data.category.path}
        title={data.category.title}
        type={data.category.type}
        open={open}
        active={isActive}
        hasChild={!!data.children}
        externalLink={isExternalLink(data.category.path)}
      />
    </div>
  );

  const renderDropdown = () =>
    !!data.children && (
      <NavDropdown
        open={open}
        indicatorLeft={indicatorLeft}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={onClose}
        ref={navContainerRef}
      >
        <Nav>
          <NavUl className="grid lg:grid-cols-4  xl:grid-cols-4 flex-row flex-wrap gap-2 xl:gap-6">
            {data.children.map((list, index) => (
              <NavSubList
                className="border-r-2 border-light-gray pr-2 xl:pr-6 last:border-r-0"
                key={index}
                category={list.category}
                data={list.children}
              />
            ))}
          </NavUl>
        </Nav>
      </NavDropdown>
    );

  return (
    <NavLi className={className} {...other}>
      {renderNavItem()}
      {renderDropdown()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

function NavSubList({ data, category, className, ...other }: NavSubListProps) {
  const pathname = usePathname();

  return (
    <NavLi className={mergeClasses(['flex-grow flex-basis-auto', className])} {...other}>
      <NavUl>
        {category && (
          <NavLi className="mb-3 text-xs uppercase font-medium tracking-wider">
            <NavItem
              subHeader
              title={category.title}
              path={category.path}
              type={category.type}
              active={isEqualPath(category.path, pathname)}
              className="font-bold text-lg capitalize"
            />
          </NavLi>
        )}

        {data?.map((item, index) => (
          <NavLi key={index} className="mt-3">
            <NavItem
              subItem
              title={item.category.title}
              path={item.category.path}
              icon={item.category.icon}
              type={item.category.type}
              active={isEqualPath(item.category.path, pathname)}
            />
          </NavLi>
        ))}
      </NavUl>
    </NavLi>
  );
}
